<template>
  <section class="space-y-4">
    <div class="bg-white border border-gray-100 rounded-lg p-3 shadow-sm space-y-3">
      <div class="flex items-center gap-2">
        <div class="relative flex-1 min-w-0">
        <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
          <span class="material-symbols-outlined">search</span>
        </span>
        <input
          :value="searchQuery"
          type="text"
          class="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-300 text-left"
          placeholder="Search peers..."
          @input="$emit('update:searchQuery', $event.target.value)"
        />
        </div>
        <button
          type="button"
          class="h-12 w-12 rounded-lg border inline-flex items-center justify-center transition-colors relative shrink-0"
          :class="activeFilterCount ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-blue-50 hover:text-blue-700'"
          aria-label="Open filters"
          @click="$emit('open-filters')"
        >
          <span class="material-symbols-outlined">tune</span>
          <span
            v-if="activeFilterCount"
            class="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-xs flex items-center justify-center"
          >
            {{ activeFilterCount }}
          </span>
        </button>
      </div>
      <div v-if="filterChips.length || searchQuery" class="flex flex-wrap items-center gap-2 text-xs">
        <span v-if="searchQuery" class="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-blue-700">
          Search: {{ searchQuery }}
          <button
            type="button"
            class="h-4 w-4 rounded-full hover:bg-blue-100 inline-flex items-center justify-center"
            aria-label="Clear search"
            @click="$emit('update:searchQuery', '')"
          >
            <span class="material-symbols-outlined text-[14px]">close</span>
          </button>
        </span>
        <span
          v-for="chip in filterChips"
          :key="chip.key"
          class="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-700"
        >
          {{ chip.label }}: {{ chip.value }}
          <button
            type="button"
            class="h-4 w-4 rounded-full hover:bg-gray-200 inline-flex items-center justify-center"
            :aria-label="`Remove ${chip.label} filter`"
            @click="$emit('remove-filter', chip.key)"
          >
            <span class="material-symbols-outlined text-[14px]">close</span>
          </button>
        </span>
      </div>
    </div>

    <div v-if="loading && !users.length" class="flex flex-col items-center justify-center py-12">
      <div class="w-9 h-9 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
      <p class="mt-3 text-sm text-blue-800">Loading peers...</p>
    </div>

    <div v-else-if="error" class="bg-white rounded-lg border border-red-100 p-8 text-center">
      <span class="material-symbols-outlined text-4xl text-red-500">error</span>
      <p class="mt-2 text-red-600 font-medium">{{ error }}</p>
      <button
        type="button"
        class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        @click="$emit('retry')"
      >
        Try Again
      </button>
    </div>

    <div v-else-if="!users.length" class="bg-white rounded-lg border border-gray-100 p-8 text-center">
      <span class="material-symbols-outlined text-4xl text-gray-300">person_search</span>
      <p class="mt-2 text-gray-700 font-medium">No peers found</p>
      <p class="text-sm text-gray-500 mt-1">Try another search or load more peers.</p>
    </div>

    <div v-else class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
      <peer-profile-card
        v-for="user in users"
        :key="user.id"
        :user="user"
        :connection-status="getConnectionStatus(user.id)"
        @connect="$emit('connect', $event)"
        @chat="$emit('chat', $event)"
        @view="$emit('view', $event)"
      />
    </div>

    <div v-if="hasMore && !error" class="flex justify-center pt-2">
      <button
        type="button"
        :disabled="loading"
        class="px-5 py-2.5 rounded-lg bg-white border border-blue-100 text-blue-700 font-semibold hover:bg-blue-50 disabled:opacity-50 transition-colors inline-flex items-center gap-2"
        @click="$emit('load-more')"
      >
        <span v-if="loading" class="w-4 h-4 border-2 border-blue-200 border-t-blue-700 rounded-full animate-spin"></span>
        Load More
      </button>
    </div>
  </section>
</template>

<script>
import PeerProfileCard from './PeerProfileCard.vue';

export default {
  name: 'DiscoveryGrid',
  components: { PeerProfileCard },
  props: {
    users: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    hasMore: {
      type: Boolean,
      default: false
    },
    activeFilterCount: {
      type: Number,
      default: 0
    },
    filterChips: {
      type: Array,
      default: () => []
    },
    searchQuery: {
      type: String,
      default: ''
    },
    getConnectionStatus: {
      type: Function,
      required: true
    }
  },
  emits: ['connect', 'chat', 'view', 'retry', 'load-more', 'open-filters', 'update:searchQuery', 'remove-filter']
};
</script>
